import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { audioService } from "@/core/audio/audioService";

/**
 * SixNineFX — Phase 2 special signature for the "Shy Ball" number (69).
 *
 * Design philosophy (approved):
 *   • Subtle & elegant — NEVER cartoonish.
 *   • The ball's 180° digit flip IS the "looking away" gesture. This
 *     module only decorates that moment; it never draws over the number.
 *   • Warm rose-blush palette. Playful but family-friendly.
 *   • Fully layered — the sacred ball reveal is not touched.
 *
 * Timeline (from active=true, aligned with the SixNineDigitFlip inside
 * the ball):
 *
 *   0.00 → 0.35s   SACRED SETTLE. No decoration yet. Number locks in.
 *   0.35 → 0.95s   Ball rotates to 180° (the shy "looking away").
 *                  Rose halo softly blooms. Blush cheeks begin fading in.
 *   0.95 → 1.35s   Ball HELD at 180° (~0.4s glance away — within the
 *                  approved 0.3-0.5s window). Blush cheeks at peak.
 *   1.35 → 1.95s   Ball rotates back to face the audience.
 *                  Gentle smile glow blooms at the base of the ball.
 *                  Three small hearts drift up behind the ball with a
 *                  quick fade.
 *   1.95 → 2.20s   Smile fades. Hearts complete their float. Halo dies.
 *                  Tagline "Either Way Up" gently fades out.
 *
 * Sacred-number rule preserved:
 *   • All decoration lives OUTSIDE the number span. Never occludes it.
 *   • Decoration begins only after the sacred settle completes.
 */

export const SIX_NINE_VALUE = 69;
export const SIX_NINE_DURATION_MS = 2200;

const ROSE_SOFT = "rgba(249, 168, 180, 0.55)";
const ROSE_DEEP = "rgba(225, 29, 116, 0.35)";
const SMILE_WARM = "rgba(255, 196, 168, 0.55)";

export default function SixNineFX({ active }) {
  useEffect(() => {
    if (!active) return;
    audioService.playShyBall();
  }, [active]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          data-testid="phase2-shy-ball-fx"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="absolute inset-0 pointer-events-none"
        >
          {/* Subtle rose halo — much dimmer than a normal special glow. */}
          <motion.div
            aria-hidden
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0, 0.28, 0.22, 0.18, 0] }}
            transition={{
              duration: SIX_NINE_DURATION_MS / 1000,
              times: [0, 0.16, 0.35, 0.6, 0.85, 1],
              ease: "easeInOut",
            }}
            style={{
              background:
                "radial-gradient(circle at 50% 55%, rgba(225, 29, 116, 0.28) 0%, rgba(249, 168, 180, 0.10) 45%, transparent 78%)",
            }}
          />

          {/* Blush cheeks — two tiny soft pink glows on the ball's face,
              positioned below the number's centerline. Very subtle. */}
          <BlushCheek side="left" />
          <BlushCheek side="right" />

          {/* Gentle smile — a soft warm arc at the base of the white face,
              appears as the ball returns to the audience. */}
          <GentleSmile />

          {/* Three small hearts drift up from around the ball's edges
              (never crossing the sacred number). */}
          <FloatingHeart startAt={0.42} startX="18%" endX="8%" startY="60%" endY="8%" />
          <FloatingHeart startAt={0.50} startX="50%" endX="52%" startY="12%" endY="-18%" />
          <FloatingHeart startAt={0.58} startX="82%" endX="92%" startY="60%" endY="8%" />

          {/* Tiny demure tagline, sits below the ball. */}
          <div
            className="absolute inset-x-0 flex justify-center"
            style={{ bottom: "-10%" }}
          >
            <motion.span
              data-testid="phase2-shy-ball-tagline"
              className="ic-font-display font-medium tracking-[0.35em] uppercase select-none"
              style={{
                fontSize: "clamp(0.55rem, 0.75vw, 0.9rem)",
                color: "rgba(255, 220, 235, 0.55)",
                textShadow: "0 0 6px rgba(225, 29, 116, 0.25)",
              }}
              initial={{ opacity: 0, y: 6 }}
              animate={{
                opacity: [0, 0, 0.55, 0.55, 0],
                y: [6, 6, 0, 0, 0],
              }}
              transition={{
                duration: SIX_NINE_DURATION_MS / 1000,
                times: [0, 0.55, 0.68, 0.9, 1],
                ease: "easeOut",
              }}
            >
              Either Way Up
            </motion.span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * BlushCheek — one soft rose glow. Sits on the ball's white face, just
 * beside the number, low enough not to touch the digits. Very subtle,
 * ~10% of ball width, low opacity, softly blurred.
 */
function BlushCheek({ side }) {
  const leftPct = side === "left" ? "26%" : "74%";
  return (
    <motion.div
      aria-hidden
      className="absolute rounded-full"
      style={{
        width: "9%",
        height: "6%",
        left: leftPct,
        top: "62%",
        transform: "translate(-50%, -50%)",
        background: `radial-gradient(circle, ${ROSE_SOFT} 0%, rgba(249, 168, 180, 0) 70%)`,
        filter: "blur(3px)",
      }}
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{
        opacity: [0, 0, 0.85, 0.85, 0.6, 0],
        scale: [0.7, 0.7, 1, 1, 1, 0.9],
      }}
      transition={{
        duration: SIX_NINE_DURATION_MS / 1000,
        times: [0, 0.28, 0.45, 0.75, 0.9, 1],
        ease: "easeInOut",
      }}
    />
  );
}

/**
 * GentleSmile — a soft warm arc that glows at the base of the ball's
 * white face. It appears as the ball rotates BACK to face the audience
 * (the "gentle smile on return" moment). Rendered as an SVG arc so it
 * scales cleanly at any projector size.
 */
function GentleSmile() {
  return (
    <motion.div
      aria-hidden
      data-testid="phase2-shy-ball-smile"
      className="absolute"
      style={{
        left: "50%",
        top: "78%",
        width: "14%",
        height: "5%",
        transform: "translate(-50%, -50%)",
      }}
      initial={{ opacity: 0, scaleX: 0.6 }}
      animate={{
        opacity: [0, 0, 0, 0.9, 0.9, 0],
        scaleX: [0.6, 0.6, 0.6, 1, 1, 1],
      }}
      transition={{
        duration: SIX_NINE_DURATION_MS / 1000,
        // Smile blooms as the flip returns (Phase 3 = 1.35s / 2.2s ≈ 0.61)
        times: [0, 0.55, 0.62, 0.72, 0.88, 1],
        ease: "easeOut",
      }}
    >
      <svg
        viewBox="0 0 100 40"
        width="100%"
        height="100%"
        preserveAspectRatio="none"
      >
        <path
          d="M 8 6 Q 50 46 92 6"
          fill="none"
          stroke={SMILE_WARM}
          strokeWidth="6"
          strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 6px ${ROSE_DEEP})` }}
        />
      </svg>
    </motion.div>
  );
}

/**
 * FloatingHeart — one small heart drifting from the outer edge of the
 * ball upward with a quick fade. Never crosses the ball's white face,
 * so it never touches the sacred number.
 *
 * `flipDir` inverts the y motion so a heart can float DOWN below the
 * ball if desired (used for the ground heart that sinks out of frame).
 */
function FloatingHeart({ startAt, startX, endX, startY, endY, flipDir = false }) {
  const total = SIX_NINE_DURATION_MS / 1000;
  const t1 = startAt;
  const t2 = Math.min(startAt + 0.08, 0.97);
  const t3 = Math.min(startAt + 0.22, 0.99);
  return (
    <motion.div
      aria-hidden
      className="absolute"
      style={{
        width: "2.4%",
        height: "2.4%",
        transform: "translate(-50%, -50%)",
      }}
      initial={{ opacity: 0, left: startX, top: startY, scale: 0.4 }}
      animate={{
        opacity: [0, 0, 0.9, 0.55, 0],
        left: [startX, startX, startX, endX, endX],
        top: [startY, startY, startY, endY, endY],
        scale: [0.4, 0.4, 1, 0.9, 0.7],
      }}
      transition={{
        duration: total,
        times: [0, t1, t2, t3, 1],
        ease: flipDir ? "easeIn" : "easeOut",
      }}
    >
      <svg
        viewBox="0 0 100 100"
        width="100%"
        height="100%"
        style={{ filter: "drop-shadow(0 0 5px rgba(225, 29, 116, 0.6))" }}
      >
        <path
          d="M50,88 C10,60 10,30 32,22 C42,18 50,26 50,32 C50,26 58,18 68,22 C90,30 90,60 50,88 Z"
          fill="#F9A8B4"
        />
      </svg>
    </motion.div>
  );
}
